import { Component, ElementRef, EventEmitter, forwardRef, inject, Input, OnDestroy, OnInit, Output, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SlideLayout } from '@app/model/enums/slide-layout.enum';
import { SlideType, AnimationOption, QuestionType } from 'library-explorer';
import { SlideDTO } from '@app/model/slideDTO.model';
import { BaseSlideWidgetComponent } from '../base-slide-widget/base-slide-widget';
import { SlideBuilderComponent } from '../slide-builder/slide-builder.component';
import { AnswerDTO } from '@app/model/answerDTO.model';
import { IS_BRANCHING_SLIDE } from '../../constants/injection-tokens';

@Component({
    selector: 'app-question-widget',
    templateUrl: './question-widget.component.html',
    styleUrls: ['./question-widget.component.scss'],
    providers: [
      {
          provide: NG_VALUE_ACCESSOR,
          useExisting: forwardRef(() => QuestionWidgetComponent),
          multi: true
      }
    ],
    host: {
      'class': 'widget-container',
      '[class.has-animation]': 'data.animation',
      '[class.question-hidden]': 'slideHidden()',
      '[class.hint-hidden]': 'isBranchingSlide?.()'
    },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QuestionWidgetComponent extends BaseSlideWidgetComponent implements ControlValueAccessor, OnInit, OnDestroy {
  public isBranchingSlide = inject(IS_BRANCHING_SLIDE, { optional: true }) ?? signal(false);

  @ViewChild(SlideBuilderComponent) slideBuilderComponent: SlideBuilderComponent;
  @Input() public isSlideBuilder = false;
  @Input() public isHorizontal: boolean;
  @Input() public chapterContainerHeight: number;
  @Input() public set data(value: SlideDTO) {
    this._data = value;
    this.isVerticalLayout = this.verticalLayouts.indexOf(value.layout) !== -1;
  }
  @Input() public requiredToWatchVideo: boolean;
  @Input() public requiredToCompleteInteraction: boolean;

  @Output() public videoWatched: EventEmitter<SlideDTO> = new EventEmitter();
  @Output() public questionCompleted: EventEmitter<{ slide: SlideDTO, completed: boolean }> = new EventEmitter();
  @Output() public interactionCompleted: EventEmitter<{ slide: SlideDTO, completionRatio: number }> = new EventEmitter();

  public get data() {
    return this._data;
  }
  
  public disabled = false;
  public questionTypes: typeof QuestionType = QuestionType;
  public slidesTypes: typeof SlideType = SlideType;
  public answer: Partial<AnswerDTO>;
  public layouts: typeof SlideLayout = SlideLayout;
  public animations: typeof AnimationOption = AnimationOption;
  public animationDelay = 0;

  public slideHidden = signal(false);

  public readonly questionsWithLayoutOption = [
    QuestionType.BOOLEAN_QUESTION,
    QuestionType.QUESTION_MULTICHOICE_TEXT
  ] as any[];

  public readonly verticalLayouts = [
    this.layouts.TEXT_BOTTOM_MEDIA_TOP , this.layouts.TEXT_TOP_MEDIA_BOTTOM
  ];

  public isVerticalLayout = true;
  private _data: SlideDTO;

  constructor(protected readonly element: ElementRef) {
    super(element);
  }

  ngOnInit(): void {
    if (this.isSlideBuilder && this.data.animation) {
      this.slideHidden.set(true);
      this.animationDelay = this.data.animationDelay || 0;
      this.addIntersectionObserver();
    }
  }

  ngOnDestroy(): void {
    this.removeIntersectionObserver();
  }

  onChange = (data: any) => { };
  onTouched = () => { };

  public writeValue(obj: any): void {
    this.answer = obj;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public setAnswer(data = this.answer, slide = this._data): void {
    this.answer = data ? { ...data, sourceId: slide?.id } : null;  
    this.onChange(this.answer);
   
    this.questionCompleted.emit({ slide, completed: !!this.answer });
  }

  public slideAnswerChanged(data: { slide: SlideDTO, answer: Partial<AnswerDTO> }): void {
    const { slide, answer } = data;
    this.setAnswer(answer, slide);
  }

  public slideInteractionCompleted(data: { slide: SlideDTO, completionRatio: number }): void {
    this.interactionCompleted.emit(data);
  }

  public emitVideoWatched(slide = this.data): void {
    this.videoWatched.emit(slide);
  }
}
